<template>
    <div class="w-full flex justify-center">
      <div class="w-full md:container px-4 md:px-0 flex flex-col items-center justify-start">
        <div class="w-full gap-1 pt-2">
          <div class="inline-flex items-center w-full">
            <div class="flex px-2 cursor-pointer" @click="$emit('close')">
              <UIcon name="i-ph-arrow-left" class="h-5 w-5" />
            </div>
            <div class="relative flex-1">
              <UInput
                padded
                v-model="searchText"
                :autofocus="true"
                size="xl"
                :ui="{
                  color: {
                    white: {
                      outline:
                        'focus:ring-0.5  focus:ring-primary focus:bg-transparent',
                    },
                  },
                  leading: {
                    padding: {
                      xl: 'ps-10'
                    }
                  }
                }"
                @focus="onFocus"
                @keyup.enter="handleSearch"
                >
              <template #leading>
                <img src="/v2/icons/SearchBar-icon.png" class="w-4 h-4" />
              </template>
            </UInput>
              <div
                v-if="!searchText && placeholder.length"
                  class="absolute left-10 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1 text-[#828282] mt-0.25"
              >
                <span>Search</span>
                <V2ListAnimateComponent :items="placeholder" />
              </div>
              <div
                class="absolute top-2 right-14 cursor-pointer text-gray-400"
                v-if="searchText.length"
                @click="clearSearch"
              >
              </div>
              <div class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                <svg v-if="searchText.length" @click="clearSearch" class="text-primary w-5 h-5 cursor-pointer" viewBox="0 0 16 16" fill="none">
                  <path d="M12 12L8 8M8 8L4 4M8 8L12 4M8 8L4 12"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div v-if="!isLocationAvailable" class="mt-4 bg-primary-100 p-3 rounded-lg">
            <p class="leading-relaxed text-gray-800">
              Select location for more accurate search results. Showing products that are available pan India.
              <span class="text-primary-600 font-semibold underline ml-1 hover:text-primary-800" @click="$emit('openlocation')"> Select Location </span>
            </p>
          </div>
        </div>
        <div class="w-full flex flex-col gap-4 overflow-y-auto overflow-x-hidden h-svh md:h-full pt-3 mt-5">
          <div class="w-full">
            <div v-if="!isValidSearchText || emptyState" class="w-full space-y-8">
              <div class="py-2" v-if="emptyState">
                <div class="flex items-center gap-2">
                  <V3ImageComponent
                    src="icons/search-not-found.gif"
                    class="w-12 h-12 object-cover"
                  />
                  <p
                    class="text-base md:text-[28px] text-[#5B71F9] font-semibold"
                  >
                    We couldn't find any matching results for "{{ searchText }}".
                  </p>
                </div>
              </div>
              <V2RecentSearchComponent class="w-full" @selected="setSearchText" />
              <V2TrendingSearchComponent class="w-full" />
            </div>
            <div v-else class="w-full space-y-8">
              <V2SearchGroupListComponent
                v-if="loading || categories.length"
                class="w-full"
                title="Explore Categories"
                :items="categories"
                :loading="loading"
                @navigate="navigate"
              />
            <div class="relative w-full">
              <V2SearchGroupCardComponent
                v-if="loading || providers.length"
                class="w-full"
                title="Sellers"
                :items="providers"
                :loading="loading"
                @navigate="navigate"
              />
              <button
                v-if="providers.length"
                class="absolute top-1 right-2 text-primary text-[14px] font-medium underline"
                @click="$router.push({ path: '/search', query: { q: searchText, tab: 'Sellers' } })"              >
                View all
              </button>
            </div>

              <div class="relative w-full">
              <V2SearchGroupCardComponent
                v-if="loading || products.length"
                class="w-full"
                title="Products"
                :items="products"
                :loading="loading"
                @navigate="navigate"
              />
              <button
                v-if="products.length"
                class="absolute top-1 right-2 text-primary text-[14px] font-medium underline"
                @click="$router.push({ path: '/search', query: { q: searchText, tab: 'Products' } })"              >
                View all
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  
  <script setup>
import { is } from 'valibot';
import { GoogleAnalytics } from '~/google_analytics/events';
import { EventCTANames, EventParameter, EventParameterValue } from '~/google_analytics/events.enum';

  
  const props = defineProps({
    filters: { type: Object, default: {} },
  });

  const emit = defineEmits(['close', 'openlocation']);
  const route = useRoute();
  const router = useRouter()
  const analyticsEvents = useAnalyticsEvents();
  const auth = useAuth();
  const isAuthenticated = computed(() => Boolean(auth?.access_token));
  const searchText = ref('');
  const categories = ref([]);
  const providers = ref([]);
  const products = ref([]);
  const loading = ref(false);
  const emptyState = ref(false);
  // const location = useLocation();
  const location = useGeoLocation();
  const latitude = computed(() => location.selectedAddress?.gps?.latitude ?? null);
  const longitude = computed(() => location.selectedAddress?.gps?.longitude ?? null);
  const isLocationAvailable = computed(() => location.isValidLocation);
  const resultCount = computed(() => [...categories.value, ...providers.value, ...products.value].length);
  const isValidSearchText = computed(() => searchText.value?.length >= 3);
  let hasFocused = false;
  let hasTrackedInitiated = false;
  let lastSearchDurationMs = undefined;
  const placeholder = ref([]);
  const fetchPlaceholder = async () => {
    try {
      const { data: categoryResult } = await useAPIHandler("get", "/categories");
      if (categoryResult?.categories) {
        placeholder.value = categoryResult.categories
          .filter((k) => k.displayName !== "Appliances")
          .map((k) => k.displayName)
          .sort((a, b) => a.localeCompare(b));
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };
  const debouncedSearch = useDebounce(async () => {
    if (!isValidSearchText.value) return;
  
    const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    loading.value = true;
    emptyState.value = false;
    categories.value = [];
    providers.value = [];
    products.value = [];
  
    try {
      const [categoriesData, providersData, productsData] = await Promise.all([
        fetchCategories(),
        fetchProviders(),
        fetchProducts()
      ]);
  
      categories.value = categoriesData;
      providers.value = providersData;
      products.value = productsData;
      trackViewSearchResults(categories.value, providers.value, products.value);
    } finally {
      const endTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
      lastSearchDurationMs = Math.max(0, Math.round(endTime - startTime));
      loading.value = false;
      emptyState.value = resultCount.value === 0;
      useAnalytics(analyticsEvents.SEARCH_PERFORMED, {
        search_term: searchText.value.trim(),
        num_results: resultCount.value,
        search_response_time_ms: lastSearchDurationMs,
        result_status: resultCount.value > 0 ? 'results_found' : 'no_results',
        scope: 'search_modal',
      });
      GoogleAnalytics.search({
        [EventParameter.SEARCH_QUERY]: searchText.value.trim(),
        [EventParameter.NO_OF_SEARCH_RESULTS]: resultCount.value,
        [EventParameter.SEARCH_RESULT_TIME_MS]: lastSearchDurationMs,
        [EventParameter.SEARCH_RESULT_STATUS]: resultCount.value > 0 ? 'results_found' : 'no_results',
      })
    }
  }, 500);

  const trackViewSearchResults = (categories, providers, products) => {
    const items = [...categories, ...providers, ...products];
    GoogleAnalytics.viewSearchResults({
      [EventParameter.SEARCH_QUERY]: searchText.value.trim(),
      [EventParameter.SEARCH_RESULT_COUNT]: items.length,
      [EventParameter.SEARCH_RESULT_STATUS]:
        items.length > 0 ? 'results_found' : 'no_results',
      [EventParameter.CATEGORIES_COUNT]: categories.length,
      [EventParameter.PROVIDERS_COUNT]: providers.length,
      [EventParameter.PRODUCTS_COUNT]: products.length,
    });
  };
  
  watch(searchText, (value) => {
    if (!hasTrackedInitiated && value?.trim()) {
      hasTrackedInitiated = true;
      useAnalytics(analyticsEvents.SEARCH_INITIATED, {
        search_term: value.trim(),
      });
    }
    
    debouncedSearch();
  });
  
  const fetchCategories = async () => {
    const { data } = await useAPIHandler('get', '/suggestions', {
      query: {
        text: searchText.value.trim(),
        c: 1, s: 0, p: 0, limit: 20
      }
    });
  
    return data.categories.slice(0, 3).map(({ name, parent, search_tag }) => ({
      label: name,
      suffix: `in ${parent}`,
      icon: 'i-ph-arrow-up-right',
      href: `/categories/search-v2?parent=${encodeURIComponent(search_tag)}&q=${encodeURIComponent(name)}`
    }));
  }
  
  const fetchProviders = async () => {
    const { data, error } = await useAPIHandler('post', '/v2/discover', {
      body: {
        location: {
          gps: [
            isLocationAvailable.value ? latitude.value : 0,
            isLocationAvailable.value ? longitude.value : 0,
          ],
        },
        ...(props.filters.categories ? { categories: props.filters.categories } : {}),
        ...(props.filters.providers ? { providers: props.filters.providers } : {}),
        ...(props.filters.max_distance ? { max_distance: props.filters.max_distance } : {}),
        partner_id: useRuntimeConfig().public.partnerCode,
        search_text: searchText.value.toLowerCase(),
        fields: ['ukid', 'name', 'images', 'categories', 'locations', 'distance', 'is_open_now', 'is_deliverable'],
        sort: [{ field: 'distance', order: 'asc' }],
        page_size: 20,
        partner_id: useRuntimeConfig().public.partnerCode 
      }
    });
    if (error) return [];
  
    const getPriority = ({ is_open_now, is_deliverable }) =>
      is_open_now && is_deliverable ? 0 : is_deliverable ? 1 : 2;
  
    const formatDistance = (distance) =>
      distance <= 1000
        ? `${Math.ceil(distance / 10) * 10} m`
        : `${(distance / 1000).toFixed(1)} km`;
  
    return data.providers
      .sort((a, b) => getPriority(a) - getPriority(b))
      .map(({ name, distance, categories, is_open_now, is_deliverable, images, locations, ukid }) => {
        const uniqCategories = [...new Set(categories.filter(item => item !== null))];
        const messages = [
          ...(uniqCategories.length ? [uniqCategories[0]] : []),
          ...(Number.isFinite(distance) ? [formatDistance(distance)] : []),
          ...(uniqCategories.length && uniqCategories[0].toUpperCase() === 'F&B' ? [locations.address.locality] : [locations.address.city])
        ];
        const tags = [];
        if (!is_open_now) tags.push('Closed');
        if (is_open_now && !is_deliverable) tags.push('Non-deliverable');
  
        return {
          label: name,
          avatar: images.thumbnail,
          message: messages.join(' | '),
          tags,
          href: `/providers/${ukid}?loc_id=${locations.id}`
        };
      });
  };
  
  const fetchProducts = async () => {
    const { path, query } = route;
  
    const page = path.includes('/search-v2')
    ? 'subcategory'
    : path.includes('/categories')
    ? 'category'
    : 'home';
  
    const categorySlugToName = {
    fandb: "F&B",
    handk: "Home & Kitchen",
    handw: "Health & Wellness"
    };
  
    const rawCategory = query.parent || null;
    const category = rawCategory ? categorySlugToName[rawCategory] || rawCategory : null;
  
    const subcategory = page === 'subcategory' ? query.q || null : null;
  
    const { data, error } = await useAPIHandler('post', '/v2/search', {
      body: {
       gps: isLocationAvailable.value
        ? {
            lat: latitude.value,
            lng: longitude.value,
          }
        : {
            lat: 0,
            lng: 0,
          },
        partner_id: useRuntimeConfig().public.partnerCode,
        ...(category ? { categories: [category] } : {}),
        ...(subcategory ? { sub_categories: [subcategory] } : {}),
        text: searchText.value.toLowerCase(),
        limit: 20,
         partner_id: useRuntimeConfig().public.partnerCode
      }
    });
  
    if (error) return [];
    return data.map(({ name, category, images, stock, provider, sku_id, time_to_ship, max_price, price }) => {
    const isOutOfStock = stock ? Number(stock.available) === 0 : false;
  
    return {
      label: name,
      avatar: images.thumbnail,
      max_price,
      price,
      message: {
        category: `<span>${category.name}</span>`,
        provider: `<span class="text-gray-600">${provider.name}</span>`,
        isOutOfStock: isOutOfStock ? `<span class="text-red-500 font-semibold">Out of stock</span>` : ''
      },
      href: `/products/${sku_id}`
    };
  });
  
  };
  
  const updateSearchHistory = (value) => {
    const searchesRaw = localStorage.getItem('searches');
    const searches = searchesRaw ? JSON.parse(searchesRaw) : [];
      
    const normalizedText = value.toLowerCase();
    const uniqueSearchesMap = new Map(
      searches.map((item) => [item.name.toLowerCase(), item])
    );
    uniqueSearchesMap.delete(normalizedText);
    const uniqueSearches = Array.from(uniqueSearchesMap.values());
    if (uniqueSearches.length === 5) {
      uniqueSearches.pop();
    }
    uniqueSearches.unshift({ name: value });
    localStorage.setItem('searches', JSON.stringify(uniqueSearches));
  }
  
  const setSearchText = async (value) => {
    const typedTerm = searchText.value;
    searchText.value = value;
    updateSearchHistory(value);
    GoogleAnalytics.searchSuggestionClicked({
        [EventParameter.SEARCH_QUERY]: value,
        [EventParameter.SEARCH_SUGGESTION_CLICKED]: value
    });
  }
  const navigate = (url) => {
    const newURL = new URL(url, window.location.origin);
    const isCategoriesRoute = route.path.includes('/categories');
    const query = Object.fromEntries(newURL.searchParams.entries());
    const pathParts = newURL.pathname.split('/').filter(Boolean);
    const section = pathParts[0]; // "providers" or "products"
    const rawItemId = pathParts[1];
    const cleanItemId = rawItemId.split("?")[0]; // ✅ remove query params
  
    let clickedName = null;
  
    if (section === 'products') {
      const foundProduct = products.value.find(p => {
        const productIdFromHref = p.href.split("/products/")[1];
        return productIdFromHref === cleanItemId;
      });
      clickedName = foundProduct ? foundProduct.label : null;
    } 
    else if (section === 'providers') {
      const foundProvider = providers.value.find(p => {
        const providerIdFromHref = p.href.split("/providers/")[1].split("?")[0];
        return providerIdFromHref === cleanItemId;
      });
      clickedName = foundProvider ? foundProvider.label : null;
    } else if (section === 'categories') {
      const foundCategory = categories.value.find(c => c.href === url);
      clickedName = foundCategory ? foundCategory.label : null;
    }
  
    const targetPath = newURL.pathname + (newURL.search ? newURL.search : '');
    const currentFullPath = route.fullPath;
  
    // Normalize paths for comparison
    const normalizePath = (path) => decodeURIComponent(path.replace(/\+/g, ' '));
  
    if (normalizePath(targetPath) === normalizePath(currentFullPath)) {
      window.location.href = targetPath;
      return;
    }

    if (section === 'providers' || section === 'products' || section === 'categories') {
      let resultPosition;
      if (section === 'products') {
        const productIndex = products.value.findIndex((p) => {
          const productIdFromHref = p.href.split("/products/")[1];
          return productIdFromHref === cleanItemId;
        });
        if (productIndex !== -1) {
          resultPosition = productIndex + 1;
        }
      } else if (section === 'providers') {
        const providerIndex = providers.value.findIndex((p) => {
          const providerIdFromHref = p.href.split("/providers/")[1].split("?")[0];
          return providerIdFromHref === cleanItemId;
        });
        if (providerIndex !== -1) {
          resultPosition = providerIndex + 1;
        }
      } else if (section === 'categories') {
        const categoryIndex = categories.value.findIndex((c) => c.href === url);
        if (categoryIndex !== -1) {
          resultPosition = categoryIndex + 1;
        }
      }
      useAnalytics(analyticsEvents.SEARCH_SUGGESTION_CLICKED, {
        search_term: searchText.value,
        clicked_result: clickedName || cleanItemId,
        result_position: typeof resultPosition === 'number' ? resultPosition : undefined,
      });
      GoogleAnalytics.searchSuggestionClicked({
        [EventParameter.SEARCH_QUERY]: searchText.value,
        [EventParameter.SEARCH_SUGGESTION_CLICKED]: clickedName || cleanItemId
    });
    }
    if (section === 'providers') {
      logCtaEvent({ type: 'provider', data: cleanItemId, isCategoriesRoute });
    } else if (section === 'products') {
      logCtaEvent({ type: 'product', data: cleanItemId, isCategoriesRoute });
    }
    navigateTo({ path: url, query });
    updateSearchHistory(searchText.value);
  }
  
  const onFocus = () => {
    if (hasFocused) return;
    hasFocused = true;
    if (!hasTrackedInitiated) {
      hasTrackedInitiated = true;
      useAnalytics(analyticsEvents.SEARCH_INITIATED, {
        search_term: searchText.value?.trim() || undefined,
      });
    }

    GoogleAnalytics.searchOpen({
      [EventParameter.PLACEMENT]: EventParameterValue.HEADER
    });

  };
  const logCtaEvent = ({ type, data, isCategoriesRoute }) => {
  
    if (type === 'provider') {
  
      GoogleAnalytics.searchSuggestionClicked({
        [EventParameter.SEARCH_QUERY]: searchText.value,
        [EventParameter.CTA_NAME]: EventCTANames.SEARCH_PROVIDER_CTA,
        [EventParameter.PROVIDER_NAME]: data,
        [EventParameter.SEARCH_PERFORMED_SCREEN]: isCategoriesRoute ? EventParameterValue.CATEGORY_SCREEN : EventParameterValue.HOME_SCREEN
        
      });
    } else if (type === 'product') {
      
      GoogleAnalytics.searchSuggestionClicked({
        [EventParameter.SEARCH_QUERY]: searchText.value,
        [EventParameter.CTA_NAME]: EventCTANames.SEARCH_PRODUCT_CTA,
        [EventParameter.PRODUCT_NAME]: data,
        [EventParameter.SEARCH_PERFORMED_SCREEN]: isCategoriesRoute ? EventParameterValue.CATEGORY_SCREEN : EventParameterValue.HOME_SCREEN
      });
    }
  };
  
  const clearSearch = () => {
    GoogleAnalytics.cta({
      [EventParameter.CTA_NAME]: EventCTANames.CLEAR_SEARCH,
      [EventParameter.SEARCH_QUERY]: searchText?.value,
    });
    searchText.value = ''
  }
  
  const handleSearch = () => {
    updateSearchHistory(searchText.value);
    if (searchText.value && isValidSearchText.value && !emptyState.value) {
      const parentCategory = route.path.includes('/categories')
        ? route.query.parent
        : null;
  
      let searchUrl = parentCategory
        ? `/search?c=${encodeURIComponent(parentCategory)}&q=${encodeURIComponent(searchText.value)}`
        : `/search?q=${encodeURIComponent(searchText.value)}`;
  
      router.push(searchUrl);
    }
  }
  onMounted(() => {
    fetchPlaceholder();
  });

  

</script>

<style>
.icon {
  color: var(--color-primary);
}
</style>